import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Layout } from 'antd';
import { Helmet } from 'react-helmet';

import Footer from './Footer';
import './Dashboard.scss';
import TopNav from '../TopNav';
import MasqueradingBanner from 'components/Masquerading/MasqueradingBanner';
import TabStore from 'components/TopNav/Tabs/TabStore';
import OnboardingModule from 'components/Onboarding/OnboardingModule';
import { RequestBanner } from 'components/RequestBanner';

import { useStoreDetails } from 'api/store';
import { useOwnerDetails } from 'api/owners';
import { useSignedUser } from 'api/auth';

const Dashboard = ({ className, children, page, doNotShowNav }) => {
  const { storeId } = useParams();
  const { data: storeData } = useStoreDetails(storeId);

  const { data: user } = useSignedUser();
  const isMasquerading = useMemo(() => user?.is_masqueraded, [user]);

  const { data: bannerData, isLoading: isBannerLoading } = useOwnerDetails(user?.owner_id);
  const showRequestsBanner = useMemo(
    () => !isBannerLoading && bannerData.hasRequests,
    [isBannerLoading, bannerData],
  );

  if (!storeData) {
    return null;
  }

  return (
    <>
      {isMasquerading && <MasqueradingBanner identifier={user.email} />}
      {!doNotShowNav && <TopNav inStore page={page} />}
      <Layout className={clsx('Dashboard', isMasquerading && 'masquerading')}>
        <Helmet>
          {storeData?.storeName && <title>{storeData.storeName} | The Host Co</title>}
        </Helmet>

        <div className={clsx(className, 'Dashboard_desktop')}>
          {!doNotShowNav && (
            <>
              <aside className="sider" tabIndex={0}>
                <TabStore mode="inline" store={storeData} initialActiveTab="dashboard" />
              </aside>
              <div className="sider-backdrop" />
              <Layout className="Dashboard_layout">
                <div className="Dashboard_supportText">
                  <span className="supportTextLabel">Questions? Text us</span>
                  <span className="supportTextNumber">+1 (442) 269-4808</span>
                  <span className="supportTextHours">9-5 EST</span>
                </div>
                <RequestBanner visible={showRequestsBanner} />
                <OnboardingModule storeId={storeId} />
                <Layout.Content className="Dashboard_content">{children}</Layout.Content>
                <Layout.Footer className="Dashboard_footer desktop">
                  <Footer />
                </Layout.Footer>
              </Layout>
            </>
          )}
        </div>
      </Layout>
    </>
  );
};

Dashboard.propTypes = {
  className: PropTypes.string,
  page: PropTypes.string.isRequired,
  children: PropTypes.node,
  doNotShowNav: PropTypes.bool,
  defaultStore: PropTypes.object,
};

export default Dashboard;
